<template>
  <div class="figureLine">
    <canvas :ref="(el) => lineChartRef = el"></canvas>
  </div>
</template>

<script setup lang="ts">
  import {ref, onMounted, onUnmounted, watch} from "vue";
  import Chart from 'chart.js/auto' // NFR-6251 4.x では “auto” 推奨（core+plugin自動登録）
  import type { ChartOptions } from 'chart.js'

  const lineChartRef = ref<HTMLCanvasElement | null>(null)
  const props = defineProps<{
    chartData: any;
    name: string
  }>();
  const options : ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const,
      intersect: true,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(221, 221, 221, 1)",
        borderColor: "rgba(0, 0, 0, 1)",
        titleFont: {
          size: 17,
          family: 'BIZ UDGothic',
          weight: 700,
        },
        titleColor: "rgba(51, 51, 51, 1)",
        bodyFont: {
          size: 14,
          family: 'BIZ UDGothic',
          weight: 700,
        },
        bodyColor: "rgba(51, 51, 51, 1)",
        bodySpacing: 10,
        padding: 10,
        callbacks: {
          label(ctx) {
            const groupName = ctx.dataset.label
            const totalMin = Math.ceil(ctx.parsed.y * 60)
            const h = Math.floor(totalMin / 60)
            const m = totalMin % 60
            return `${groupName}：${h}時間 ${m}分`
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: "transparent",
        },
        ticks: {
          color: "rgba(51, 51, 51, 1)",
          font: {
            size: 11,
            family: 'oswald, sans-serif',
          },
        },
      },
      y: {
        position: 'left',
        grid: {
          color: (context) => context.tick.value === 0 ? "rgba(51, 51, 51, 1)" : "rgba(221, 221, 221, 1)",
          lineWidth: (context) =>  context.tick.value === 0 ? 2 : 1,
        },
        border: {
          display: false,
          color: "rgba(51, 51, 51, 1)",
        },
        ticks: {
          color: "rgba(51, 51, 51, 1)",
          font: {
            size: 16,
            family: 'oswald, sans-serif',
          },
          stepSize: 1,
        },
        suggestedMin: 0,
        suggestedMax: 2,
      },
    },
    layout: {
      padding: {
        top: 15
      }
    },
  }
  // NFR-6251 Chart インスタンス
  let chartInstance: Chart<'line'> | null = null

  // NFR-6251 チャート描画用の関数
  function renderChart() {
    if (lineChartRef.value) {
      chartInstance = new Chart(lineChartRef.value, {
        type: 'line',
        data: props.chartData,
        options
      })
    }
  }

  onMounted(() => {
    renderChart()
  })

  watch(
    () => props.chartData,
    () => {
      chartInstance?.destroy()
      chartInstance = null
      if (lineChartRef.value) renderChart()
    },
    { deep: true }
  )

  onUnmounted(() => {
    chartInstance?.destroy()
    chartInstance = null
  })
</script>
